import { z } from 'zod'

// =============================================================================
// SMS Action
// =============================================================================

export const smsActionSchema = z.object({
  id: z.string(),
  name: z.string().trim().min(1, 'name_required'),
  scenario: z.string().trim().min(1, 'scenario_required'),
  agent_response: z.string().trim().min(1, 'agent_response_required'),
  message_template: z.string().trim().min(1, 'message_required'),
  from_number: z.string().default('agent'),
})
export type SMSAction = z.infer<typeof smsActionSchema>

export function newSMSAction(): SMSAction {
  return {
    id: `sms_${Date.now()}`,
    name: '',
    scenario: '',
    agent_response: '',
    message_template: '',
    from_number: 'agent',
  }
}

// =============================================================================
// Transfer Action
// =============================================================================

export const transferTypeSchema = z.enum([
  'agent',
  'team',
  'external_number',
  'main_line',
])
export type TransferType = z.infer<typeof transferTypeSchema>

export const transferModeSchema = z.enum(['cold', 'warm'])
export type TransferMode = z.infer<typeof transferModeSchema>

export const ringStrategySchema = z.enum([
  'PARALLEL',
  'SEQUENTIAL',
  'RANDOM',
  'ROUND_ROBIN',
  'LEAST_RECENT',
])
export type RingStrategy = z.infer<typeof ringStrategySchema>

export const transferActionSchema = z
  .object({
    id: z.string(),
    name: z.string().trim().min(1, 'name_required'),
    scenario: z.string().trim().min(1, 'scenario_required'),
    agent_response: z.string().trim().min(1, 'agent_response_required'),
    transfer_type: transferTypeSchema,
    transfer_to: z.string(),
    transfer_to_name: z.string(),
    transfer_mode: transferModeSchema,
    ring_strategy: ringStrategySchema.optional(),
    ring_timeout: z.number().int().min(5).max(60).optional(),
    context_prompt: z.string().optional(),
    handoff_message: z.string().optional(),
    fallback_message: z.string().optional(),
  })
  // Validation conditionnelle : la destination est requise sauf en main_line.
  .refine(
    (v) => v.transfer_type === 'main_line' || !!v.transfer_to,
    { path: ['transfer_to'], message: 'transfer_to_required' },
  )
export type TransferAction = z.infer<typeof transferActionSchema>

export function newTransferAction(): TransferAction {
  return {
    id: `transfer_${Date.now()}`,
    name: '',
    scenario: '',
    agent_response: '',
    transfer_type: 'external_number',
    transfer_to: '',
    transfer_to_name: '',
    transfer_mode: 'cold',
    ring_strategy: 'PARALLEL',
    ring_timeout: 20,
    context_prompt: '',
    handoff_message: '',
    fallback_message: '',
  }
}

// =============================================================================
// Notify Admin Action
// =============================================================================

export const collectFieldTypeSchema = z.enum([
  'text',
  'phone',
  'email',
  'choice',
  'boolean',
  'number',
])
export type CollectFieldType = z.infer<typeof collectFieldTypeSchema>

export const collectFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: collectFieldTypeSchema,
  required: z.boolean(),
  options: z.array(z.string()).optional(),
})
export type CollectField = z.infer<typeof collectFieldSchema>

export const notificationMethodSchema = z.enum(['sms', 'email', 'both'])
export type NotificationMethod = z.infer<typeof notificationMethodSchema>

export const notifyTimingSchema = z.enum(['realtime', 'post_call'])
export type NotifyTiming = z.infer<typeof notifyTimingSchema>

export const notifyAdminActionSchema = z
  .object({
    id: z.string(),
    name: z.string().trim().min(1, 'name_required'),
    scenario: z.string().trim().min(1, 'scenario_required'),
    agent_response: z.string().trim().min(1, 'agent_response_required'),
    notification_method: notificationMethodSchema,
    admin_phone: z.string().optional(),
    admin_email: z.string().optional(),
    collect_fields: z.array(collectFieldSchema),
    message_template: z.string().trim().min(1, 'message_required'),
    timing: notifyTimingSchema,
  })
  .superRefine((v, ctx) => {
    if (
      (v.notification_method === 'sms' || v.notification_method === 'both') &&
      !v.admin_phone?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['admin_phone'],
        message: 'admin_phone_required',
      })
    }
    if (
      (v.notification_method === 'email' || v.notification_method === 'both') &&
      !v.admin_email?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['admin_email'],
        message: 'admin_email_required',
      })
    }
  })
export type NotifyAdminAction = z.infer<typeof notifyAdminActionSchema>

export function newNotifyAdminAction(): NotifyAdminAction {
  return {
    id: `notify_admin_${Date.now()}`,
    name: '',
    scenario: '',
    agent_response: '',
    notification_method: 'sms',
    admin_phone: '',
    admin_email: '',
    collect_fields: [],
    message_template: '',
    timing: 'realtime',
  }
}

/** Slug stable utilisé comme clé de variable dans le template. */
export function slugifyFieldName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
}
